import moment from 'moment';
import { naturalSort } from '@/helpers/util';
import { CREATE_MONITOR, DELETE_MONITOR, DISABLE_MONITOR, ENABLE_MONITOR, FETCH_MONITORS, UPDATE_MONITOR } from '@/store/types/actions';
import { MONITORS_FETCHED, MONITOR_DELETED, MONITOR_DISABLED, MONITOR_ENABLED, MONITOR_KPI_CREATED, MONITOR_KPI_DELTA_CREATED, MONITOR_KPI_DELTA_UPDATED, MONITOR_KPI_UPDATED, MONITOR_TRIGGER_CREATED, MONITOR_TRIGGER_UPDATED } from '@/store/types/mutations';
import 'moment-timezone';
import { apolloSdk } from '../../api/apollo';

const updateStateMonitor = (state, monitor) => {
  const index = state.monitors.findIndex((m) => m.id === monitor.id);
  state.monitors.splice(index, 1, {
    ...state.monitors[index],
    ...monitor,
  });
};

const toMonitorBaseInput = (monitor) => ({
  name: monitor.name,
  expression: monitor.expression,
  actions: monitor.actions,
});

const toMonitorTriggerInput = (monitor) => ({
  ...toMonitorBaseInput(monitor),
  scope: monitor.scope,
  captureProperties: monitor.captureProperties,
  rag: monitor.rag ? {
    lower: monitor.rag.lower, upper: monitor.rag.upper, reverse: monitor.rag.reverse,
  } : null,
});

const toMonitorKpiInput = (monitor) => ({
  ...toMonitorBaseInput(monitor),
  timezone: moment.tz.guess(),
  kpi: JSON.stringify(monitor.kpi.toSaveObject().kpi),
  period: monitor.period,
  cadence: typeof monitor.cadence === 'string' ? monitor.cadence : monitor.cadence.id,
  trackAccountTime: monitor.trackAccountTime,
});

const toMonitorKpiDeltaInput = (monitor) => ({
  ...toMonitorBaseInput(monitor),
  timezone: moment.tz.guess(),
  kpi: JSON.stringify(monitor.kpi.toSaveObject().kpi),
  period: monitor.period,
  cadence: typeof monitor.cadence === 'string' ? monitor.cadence : monitor.cadence.id,
  trackAccountTime: monitor.trackAccountTime,
});

const actions = {
  async [FETCH_MONITORS]({ commit }, accountId) {
    const response = await apolloSdk.Monitors({ accountId }, {
      fetchPolicy: 'network-only',
    });
    return commit(MONITORS_FETCHED, response.node.monitors);
  },
  async [CREATE_MONITOR]({ commit }, { accountId, input: monitor }) {
    if (!monitor.kpi) {
      const input = toMonitorTriggerInput(monitor);
      const response = await apolloSdk.AddMonitorTrigger({ accountId, input }, {
        refetchQueries: ['Monitors'],
      });
      return commit(MONITOR_TRIGGER_CREATED, response.addMonitorTrigger.monitor);
    }

    const { kpi } = monitor;
    if (kpi.sxs && kpi.sxs.duration) {
      const input = toMonitorKpiDeltaInput(monitor);
      const response = await apolloSdk.AddMonitorKpiDelta({ accountId, input }, {
        refetchQueries: ['Monitors'],
      });
      return commit(MONITOR_KPI_DELTA_CREATED, response.addMonitorKpiDelta.monitor);
    }

    const input = toMonitorKpiInput(monitor);
    const response = await apolloSdk.AddMonitorKpi({ accountId, input }, {
      refetchQueries: ['Monitors'],
    });
    return commit(MONITOR_KPI_CREATED, response.addMonitorKpi.monitor);
  },
  async [ENABLE_MONITOR]({ commit }, nodeId) {
    const response = await apolloSdk.EnableMonitor({ nodeId });
    return commit(MONITOR_ENABLED, response.enableMonitor.monitor);
  },
  async [DISABLE_MONITOR]({ commit }, nodeId) {
    const response = await apolloSdk.DisableMonitor({ nodeId });
    return commit(MONITOR_DISABLED, response.disableMonitor.monitor);
  },
  async [DELETE_MONITOR]({ commit }, nodeId) {
    const response = await apolloSdk.RemoveMonitor({ nodeId }, {
      refetchQueries: ['Monitors'],
    });
    return commit(MONITOR_DELETED, response.removeMonitor.monitor);
  },
  async [UPDATE_MONITOR]({ commit }, { nodeId, input: monitor }) {
    if (!monitor.kpi) {
      const input = {
        version: monitor.version,
        ...toMonitorTriggerInput(monitor),
      };
      const response = await apolloSdk.UpdateMonitorTrigger({ nodeId, input }, {
        refetchQueries: ['Monitors'],
      });
      return commit(MONITOR_TRIGGER_UPDATED, response.updateMonitorTrigger.monitor);
    }

    const { kpi } = monitor;
    if (kpi.sxs && kpi.sxs.duration) {
      const input = {
        version: monitor.version,
        ...toMonitorKpiDeltaInput(monitor),
      };
      const response = await apolloSdk.UpdateMonitorKpiDelta({ nodeId, input }, {
        refetchQueries: ['Monitors'],
      });
      return commit(MONITOR_KPI_DELTA_UPDATED, response.updateMonitorKpiDelta.monitor);
    }

    const input = {
      version: monitor.version,
      ...toMonitorKpiInput(monitor),
    };
    const response = await apolloSdk.UpdateMonitorKpi({ nodeId, input }, {
      refetchQueries: ['Monitors'],
    });
    return commit(MONITOR_KPI_UPDATED, response.updateMonitorKpi.monitor);
  },
};

const mutations = {
  [MONITORS_FETCHED](state, monitors) {
    state.monitors = naturalSort(monitors, 'name');
  },
  [MONITOR_TRIGGER_CREATED](state, monitor) {
    state.monitors = naturalSort([...state.monitors, monitor], 'name');
  },
  [MONITOR_KPI_CREATED](state, monitor) {
    state.monitors = naturalSort([...state.monitors, monitor], 'name');
  },
  [MONITOR_KPI_DELTA_CREATED](state, monitor) {
    state.monitors = naturalSort([...state.monitors, monitor], 'name');
  },
  [MONITOR_ENABLED](state, monitor) {
    updateStateMonitor(state, monitor);
  },
  [MONITOR_DISABLED](state, monitor) {
    updateStateMonitor(state, monitor);
  },
  [MONITOR_DELETED](state, monitor) {
    const index = state.monitors.findIndex((m) => m.id === monitor.id);
    state.monitors.splice(index, 1);
  },
  [MONITOR_TRIGGER_UPDATED](state, monitor) {
    updateStateMonitor(state, monitor);
  },
  [MONITOR_KPI_UPDATED](state, monitor) {
    updateStateMonitor(state, monitor);
  },
  [MONITOR_KPI_DELTA_UPDATED](state, monitor) {
    updateStateMonitor(state, monitor);
  },
};

const getters = {
  monitors: (state) => state.monitors,
};

const state = {
  monitors: [],
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
